import { Signal, computed } from '@angular/core';

/**
 * Creates a computed signal that filters items based on search text using a single property.
 *
 * @param items - The items to filter
 * @param searchText - The search text signal
 * @param searchProperty - The property name to search in
 * @param selectedValue - Optional signal containing the selected item to always include in results
 */
export function createSearchFilter<T>(
  items: Signal<T[] | undefined>,
  searchText: Signal<string>,
  searchProperty: keyof T,
  selectedValue?: Signal<T | undefined>,
): Signal<T[] | undefined>;

/**
 * Creates a computed signal that filters items based on search text using multiple properties.
 *
 * @param items - The items to filter
 * @param searchText - The search text signal
 * @param searchProperties - Array of property names to search in
 * @param selectedValue - Optional signal containing the selected item to always include in results
 */
export function createSearchFilter<T>(
  items: Signal<T[] | undefined>,
  searchText: Signal<string>,
  searchProperties: (keyof T)[],
  selectedValue?: Signal<T | undefined>,
): Signal<T[] | undefined>;

/**
 * Creates a computed signal that filters items based on search text using a custom search function.
 *
 * @param items - The items to filter
 * @param searchText - The search text signal
 * @param searchKeyGetter - Function to extract the searchable text from an item
 * @param selectedValue - Optional signal containing the selected item to always include in results
 *
 * @example
 * ```typescript
 * // Single property search (most common usage)
 * const filteredCountryCodes = createSearchFilter(
 *   this.rawCountryCodes,
 *   this.countrySearchText,
 *   'name'
 * );
 *
 * // Multiple properties search
 * const filteredByNameAndEmail = createSearchFilter(
 *   this.employees,
 *   this.searchText,
 *   ['name', 'email']
 * );
 *
 * // Custom search function for complex formatting
 * const filteredOccupationCodes = createSearchFilter(
 *   this.occupationCodes,
 *   this.occupationCodeSearchText,
 *   (code) => `${code.code} - ${code.name}`
 * );
 *
 * // With selected value to limit results and ensure selection is visible
 * const filteredOccupationCodes = createSearchFilter(
 *   this.occupationCodes,
 *   this.occupationCodeSearchText,
 *   'displayName',
 *   this.selectedOccupationCodeSignal
 * );
 * ```
 */
export function createSearchFilter<T>(
  items: Signal<T[] | undefined>,
  searchText: Signal<string>,
  searchKeyGetter: (item: T) => string,
  selectedValue?: Signal<T | undefined>,
): Signal<T[] | undefined>;

export function createSearchFilter<T>(
  itemsSignal: Signal<T[] | undefined>,
  searchTextSignal: Signal<string>,
  searchKey: keyof T | (keyof T)[] | ((item: T) => string),
  selectedValueSignal?: Signal<T | undefined>,
): Signal<T[] | undefined> {
  return computed(() => {
    const search = searchTextSignal() ?? '';
    const itemList = itemsSignal();
    const selectedValue = selectedValueSignal?.();

    if (!itemList) {
      return itemList;
    }

    const normalizedSearchText = search.trim().toLowerCase();

    const filteredItems = itemList.filter((item) => {
      let itemText: string;

      if (typeof searchKey === 'function') {
        // Custom search function
        itemText = searchKey(item);
      } else if (Array.isArray(searchKey)) {
        // Multiple properties
        itemText = searchKey.map((prop) => String(item[prop] ?? '')).join(' ');
      } else {
        // Single property
        itemText = String(item[searchKey] ?? '');
      }

      const normalizedItemText = itemText.toLowerCase();
      return normalizedItemText.includes(normalizedSearchText);
    });

    // If selectedValueSignal is defined, limit to 100 items and ensure selected value is included
    if (selectedValueSignal !== undefined) {
      const topFilteredItems = filteredItems.slice(0, 100);

      // If searching or no selected value, return top filtered items
      if (!selectedValue || search) {
        return topFilteredItems;
      }

      const hasSelectedValue = topFilteredItems.some((item) => item === selectedValue);

      // If selected value is already in top items, return them; otherwise include selected value
      if (hasSelectedValue) {
        return topFilteredItems;
      } else {
        return [selectedValue, ...topFilteredItems];
      }
    }

    return filteredItems;
  });
}
